/*
  The board report. Drawn with jsPDF rather than photographed off the screen, so
  the text stays text: selectable, searchable and sharp on paper.
*/

import { jsPDF } from 'jspdf';

import { formatMoney, formatNumber, formatPeriod } from './period';
import { buildSeries, cashflowLines } from './statistics';
import { statisticsFilename } from './statisticsCsv';

const PAGE = { width: 210, height: 297, margin: 14 };
const CONTENT = PAGE.width - 2 * PAGE.margin;
const RIGHT = PAGE.width - PAGE.margin;
const BOTTOM = PAGE.height - 20;

const colors = {
  brand: [0, 123, 180],
  teal: [0, 187, 189],
  ink: [33, 37, 41],
  muted: [120, 128, 136],
  line: [222, 226, 230],
  zebra: [247, 249, 251],
  tile: [241, 246, 250],
  good: [58, 131, 55],
  bad: [200, 71, 88],
  gold: [229, 157, 35],
  white: [255, 255, 255],
};

const toneColor = (tone) =>
  ({ in: colors.good, out: colors.bad, neutral: colors.brand }[tone] ?? colors.ink);

const setText = (doc, { size, style = "normal", color = colors.ink }) => {
  doc.setFont("helvetica", style);
  doc.setFontSize(size);
  doc.setTextColor(...color);
};

// Anything wider than its column is cut rather than wrapped: a table row that
// grows a second line would push every later row off the grid.
const clip = (doc, text, width) => doc.splitTextToSize(String(text ?? ""), width)[0] ?? "";

const newPage = (doc, cursor) => {
  doc.addPage();
  cursor.y = PAGE.margin + 4;
};

const ensure = (doc, cursor, height) => {
  if (cursor.y + height > BOTTOM) {
    newPage(doc, cursor);
  }
};

const drawHeader = (doc, report) => {
  doc.setFillColor(...colors.brand);
  doc.rect(0, 0, PAGE.width, 34, "F");

  setText(doc, { size: 21, style: "bold", color: colors.white });
  doc.text("Statistieken", PAGE.margin, 16);

  setText(doc, { size: 10, color: colors.white });
  doc.text(
    `${report.wallet.name} · ${report.wallet.company ?? ""}`.replace(/ · $/, ""),
    PAGE.margin,
    24
  );

  setText(doc, { size: 9, color: colors.white });
  doc.text(formatPeriod(report.period), RIGHT, 16, { align: "right" });
  doc.text(
    `${report.period.days} dagen · wallet ${report.wallet.code}`,
    RIGHT,
    24,
    { align: "right" }
  );
};

const drawFooter = (doc, report, page, pages) => {
  doc.setDrawColor(...colors.line);
  doc.setLineWidth(0.2);
  doc.line(PAGE.margin, PAGE.height - 14, RIGHT, PAGE.height - 14);

  setText(doc, { size: 8, color: colors.muted });
  doc.text(
    `TapWallet · ${report.wallet.name} (${report.wallet.code}) · gegenereerd op ${new Date(
      report.period.generatedAt
    ).toLocaleDateString("nl-BE")}`,
    PAGE.margin,
    PAGE.height - 9
  );
  doc.text(`${page} / ${pages}`, RIGHT, PAGE.height - 9, { align: "right" });
};

const sectionTitle = (doc, cursor, title, subtitle) => {
  cursor.y += 4;
  ensure(doc, cursor, 20);

  setText(doc, { size: 12, style: "bold" });
  doc.text(title, PAGE.margin, cursor.y);

  if (subtitle) {
    setText(doc, { size: 8, color: colors.muted });
    doc.text(subtitle, RIGHT, cursor.y, { align: "right" });
  }

  cursor.y += 2;
  doc.setDrawColor(...colors.brand);
  doc.setLineWidth(0.4);
  doc.line(PAGE.margin, cursor.y, RIGHT, cursor.y);
  cursor.y += 6;
};

const drawTiles = (doc, cursor, tiles) => {
  const gap = 4;
  const width = (CONTENT - gap * 2) / 3;
  const height = 24;

  tiles.forEach((tile, index) => {
    const column = index % 3;

    if (column === 0) {
      ensure(doc, cursor, height + gap);
      if (index > 0) {
        cursor.y += height + gap;
      }
    }

    const x = PAGE.margin + column * (width + gap);

    doc.setFillColor(...colors.tile);
    doc.roundedRect(x, cursor.y, width, height, 1.5, 1.5, "F");

    setText(doc, { size: 8, color: colors.muted });
    doc.text(clip(doc, tile.label, width - 6), x + 3, cursor.y + 6);

    setText(doc, { size: 14, style: "bold", color: toneColor(tile.tone) });
    doc.text(formatMoney(tile.value), x + 3, cursor.y + 14);

    setText(doc, { size: 7, color: colors.muted });
    doc.text(clip(doc, tile.hint, width - 6), x + 3, cursor.y + 20);
  });

  cursor.y += height + 8;
};

const drawFacts = (doc, cursor, facts) => {
  const width = CONTENT / facts.length;

  ensure(doc, cursor, 16);

  facts.forEach((fact, index) => {
    const x = PAGE.margin + index * width;

    setText(doc, { size: 13, style: "bold", color: colors.brand });
    doc.text(String(fact.value), x, cursor.y + 5);

    setText(doc, { size: 7.5, color: colors.muted });
    doc.text(clip(doc, fact.label, width - 2), x, cursor.y + 10);
  });

  cursor.y += 20;
};

/**
 * Two bars a period: what came in and what went out. Drawn by hand because a
 * screenshot of the chart on screen would go blurry the moment somebody prints
 * the thing.
 */
const drawChart = (doc, cursor, series) => {
  const points = series.points;
  const height = 44;
  const axisWidth = 18;

  ensure(doc, cursor, height + 14);

  const plotLeft = PAGE.margin + axisWidth;
  const plotWidth = CONTENT - axisWidth;
  const baseline = cursor.y + height;

  if (!points.length) {
    setText(doc, { size: 9, color: colors.muted });
    doc.text("Geen bewegingen in deze periode.", PAGE.margin, cursor.y + 6);
    cursor.y += 12;
    return;
  }

  const max = Math.max(...points.map((point) => Math.max(point.toppedUp, point.spent)), 1);

  doc.setLineWidth(0.15);
  for (const fraction of [0, 0.5, 1]) {
    const y = baseline - height * fraction;

    doc.setDrawColor(...colors.line);
    doc.line(plotLeft, y, plotLeft + plotWidth, y);

    setText(doc, { size: 7, color: colors.muted });
    doc.text(formatMoney(max * fraction), plotLeft - 2, y + 1, { align: "right" });
  }

  const slot = plotWidth / points.length;
  const barWidth = Math.min(slot * 0.34, 5);
  const labelStep = Math.ceil(points.length / 14);

  points.forEach((point, index) => {
    const centre = plotLeft + slot * (index + 0.5);
    const inHeight = (point.toppedUp / max) * height;
    const outHeight = (point.spent / max) * height;

    doc.setFillColor(...colors.teal);
    doc.rect(centre - barWidth - 0.4, baseline - inHeight, barWidth, inHeight, "F");

    doc.setFillColor(...colors.brand);
    doc.rect(centre + 0.4, baseline - outHeight, barWidth, outHeight, "F");

    if (index % labelStep === 0) {
      setText(doc, { size: 6.5, color: colors.muted });
      doc.text(point.label, centre, baseline + 4, { align: "center" });
    }
  });

  const legend = [
    { label: "Opgeladen", color: colors.teal },
    { label: "Verbruikt", color: colors.brand },
  ];

  legend.forEach((entry, index) => {
    const x = plotLeft + plotWidth - 46 + index * 25;

    doc.setFillColor(...entry.color);
    doc.rect(x, baseline + 6.5, 3, 3, "F");

    setText(doc, { size: 7.5, color: colors.muted });
    doc.text(entry.label, x + 4.5, baseline + 9);
  });

  cursor.y = baseline + 14;
};

const drawKing = (doc, cursor, king, grain) => {
  ensure(doc, cursor, 30);

  doc.setFillColor(...colors.gold);
  doc.roundedRect(PAGE.margin, cursor.y, CONTENT, 26, 2, 2, "F");

  setText(doc, { size: 8, style: "bold", color: colors.white });
  doc.text("DRANKKONING", PAGE.margin + 5, cursor.y + 7);

  setText(doc, { size: 16, style: "bold", color: colors.white });
  doc.text(clip(doc, king.name, CONTENT - 80), PAGE.margin + 5, cursor.y + 16);

  setText(doc, { size: 8, color: colors.white });
  doc.text(
    king.favourite
      ? `Favoriet: ${king.favourite.name} (${formatNumber(king.favourite.quantity)}x)`
      : "Geen productlijnen geregistreerd",
    PAGE.margin + 5,
    cursor.y + 22
  );

  const stats = [
    { label: "verbruikt", value: formatMoney(king.spent) },
    { label: "stuks", value: formatNumber(king.units) },
    { label: "beurten", value: formatNumber(king.visits) },
  ];

  stats.forEach((stat, index) => {
    const x = RIGHT - 5 - (stats.length - 1 - index) * 30;

    setText(doc, { size: 12, style: "bold", color: colors.white });
    doc.text(stat.value, x, cursor.y + 14, { align: "right" });

    setText(doc, { size: 7, color: colors.white });
    doc.text(stat.label, x, cursor.y + 19, { align: "right" });
  });

  cursor.y += 32;
};

const drawTable = (doc, cursor, { columns, rows, empty }) => {
  const headerHeight = 7;
  const rowHeight = 6.4;

  if (!rows.length) {
    setText(doc, { size: 9, color: colors.muted });
    doc.text(empty ?? "Geen gegevens in deze periode.", PAGE.margin, cursor.y + 2);
    cursor.y += 10;
    return;
  }

  const drawHeadings = () => {
    doc.setFillColor(...colors.brand);
    doc.rect(PAGE.margin, cursor.y, CONTENT, headerHeight, "F");

    setText(doc, { size: 8, style: "bold", color: colors.white });

    let x = PAGE.margin;
    for (const column of columns) {
      const align = column.align ?? "left";
      const offset = align === "right" ? column.width - 2 : 2;

      doc.text(column.header, x + offset, cursor.y + 4.8, { align });
      x += column.width;
    }

    cursor.y += headerHeight;
  };

  ensure(doc, cursor, headerHeight + rowHeight * 2);
  drawHeadings();

  rows.forEach((row, index) => {
    if (cursor.y + rowHeight > BOTTOM) {
      newPage(doc, cursor);
      drawHeadings();
    }

    if (index % 2 === 1) {
      doc.setFillColor(...colors.zebra);
      doc.rect(PAGE.margin, cursor.y, CONTENT, rowHeight, "F");
    }

    let x = PAGE.margin;
    columns.forEach((column, columnIndex) => {
      const align = column.align ?? "left";
      const offset = align === "right" ? column.width - 2 : 2;

      setText(doc, {
        size: 8.5,
        style: columnIndex === 0 ? "bold" : "normal",
        color: colors.ink,
      });
      doc.text(clip(doc, row[columnIndex], column.width - 4), x + offset, cursor.y + 4.4, {
        align,
      });
      x += column.width;
    });

    cursor.y += rowHeight;
  });

  doc.setDrawColor(...colors.line);
  doc.setLineWidth(0.2);
  doc.line(PAGE.margin, cursor.y, RIGHT, cursor.y);
  cursor.y += 8;
};

const LEADERBOARD_LIMIT = 15;
const PRODUCT_LIMIT = 15;

export const buildStatisticsPdf = (report) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const cursor = { y: 44 };
  const series = buildSeries(report.daily);

  drawHeader(doc, report);
  drawTiles(doc, cursor, cashflowLines(report));

  drawFacts(doc, cursor, [
    { label: "transacties", value: formatNumber(report.cashflow.transactions) },
    { label: "actieve kaarten", value: formatNumber(report.cashflow.activeCards) },
    { label: "verkochte stuks", value: formatNumber(report.cashflow.unitsSold) },
    { label: "gemiddelde transactie", value: formatMoney(report.cashflow.averageTransaction) },
    { label: "verbruik per dag", value: formatMoney(report.cashflow.averagePerDay) },
    { label: "gratis (goudkaarten)", value: formatMoney(report.cashflow.goldSpent) },
  ]);

  sectionTitle(
    doc,
    cursor,
    "Kasstroom",
    series.grain === "month" ? "per maand" : "per dag"
  );
  drawChart(doc, cursor, series);

  if (report.king) {
    sectionTitle(doc, cursor, "Drankkoning", "meest verbruikt in deze periode");
    drawKing(doc, cursor, report.king, series.grain);
  }

  sectionTitle(doc, cursor, "Topverbruikers", `top ${LEADERBOARD_LIMIT}`);
  drawTable(doc, cursor, {
    columns: [
      { header: "#", width: 10 },
      { header: "Naam", width: 58 },
      { header: "Favoriet", width: 46 },
      { header: "Stuks", width: 18, align: "right" },
      { header: "Beurten", width: 20, align: "right" },
      { header: "Verbruikt", width: 30, align: "right" },
    ],
    rows: report.leaderboard.slice(0, LEADERBOARD_LIMIT).map((row) => [
      String(row.rank),
      row.name,
      row.favourite ? `${row.favourite.name} (${row.favourite.quantity}x)` : "-",
      formatNumber(row.units),
      formatNumber(row.visits),
      formatMoney(row.spent),
    ]),
    empty: "Niemand heeft in deze periode iets verbruikt.",
  });

  sectionTitle(doc, cursor, "Meest verkocht", `top ${PRODUCT_LIMIT}`);
  drawTable(doc, cursor, {
    columns: [
      { header: "Product", width: 74 },
      { header: "Categorie", width: 54 },
      { header: "Aantal", width: 24, align: "right" },
      { header: "Omzet", width: 30, align: "right" },
    ],
    rows: report.products.slice(0, PRODUCT_LIMIT).map((row) => [
      row.name,
      row.category,
      formatNumber(row.quantity),
      formatMoney(row.revenue),
    ]),
  });

  sectionTitle(doc, cursor, "Per categorie");
  drawTable(doc, cursor, {
    columns: [
      { header: "Categorie", width: 98 },
      { header: "Aantal", width: 30, align: "right" },
      { header: "Omzet", width: 54, align: "right" },
    ],
    rows: report.categories.map((row) => [
      row.category,
      formatNumber(row.quantity),
      formatMoney(row.revenue),
    ]),
  });

  sectionTitle(doc, cursor, "Inkoop", "goedgekeurde bonnen");
  drawTable(doc, cursor, {
    columns: [
      { header: "Leverancier", width: 98 },
      { header: "Bonnen", width: 30, align: "right" },
      { header: "Bedrag", width: 54, align: "right" },
    ],
    rows: report.purchases.map((row) => [
      row.supplier,
      formatNumber(row.receipts),
      formatMoney(row.total),
    ]),
    empty: "Geen goedgekeurde bonnen in deze periode.",
  });

  const pages = doc.getNumberOfPages();

  for (let page = 1; page <= pages; page++) {
    doc.setPage(page);
    drawFooter(doc, report, page, pages);
  }

  return doc;
};

export const downloadStatisticsPdf = (report) =>
  buildStatisticsPdf(report).save(statisticsFilename(report, "pdf"));
